import { motion } from "framer-motion";

const tiers = [
    {
        key: "starter",
        name: "Starter",
        price: 2500,
        cadence: "/month",
        promise: "Foundations, wired for velocity.",
        features: [
            "1 engine live (Inbound or Outbound)",
            "ICP + offer workshop",
            "Meta pixel + Google Tags OR Clay/Instantly stack",
            "Weekly reporting cadence",
            "Up to 10 qualified meetings / month target",
        ],
        featured: false,
    },
    {
        key: "growth",
        name: "Growth",
        price: 5000,
        cadence: "/month",
        promise: "The full dual-engine, on one clock.",
        features: [
            "Both engines: Meta + Google & Clay + Instantly + RB2B",
            "Origami CRM + shared exclusion list",
            "Bi-weekly creative & sequence refreshes",
            "One-clock leadership review",
            "Target: 20–30 qualified meetings / month",
        ],
        featured: true,
    },
    {
        key: "scale",
        name: "Scale",
        price: 7500,
        cadence: "/month",
        promise: "Compound the loop. Cut cost per meeting.",
        features: [
            "Everything in Growth",
            "Multi-persona sequences (CFO, VPF, Controller, IT)",
            "Advanced Clay waterfalls + intent signals",
            "Dedicated GTM engineer + creative pod",
            "Target: 30+ qualified meetings / month",
        ],
        featured: false,
    },
];

export default function Pricing() {
    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="container-x py-24 md:py-40 rule-top rule-bottom"
        >
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
            >
                <div>
                    <span className="tag">Engagement Levels</span>
                    <h2 className="font-display uppercase text-white text-5xl md:text-7xl mt-4 leading-[0.9]">
                        Pick a level.
                        <span className="font-editorial italic font-normal normal-case text-[#e84125] tracking-normal block">
                            Run the system.
                        </span>
                    </h2>
                </div>
                <p className="font-body text-white/60 max-w-[42ch] md:text-right">
                    Monthly retainer. All engagements include the mandatory
                    <span className="text-white">
                        {" "}
                        one-time initial setup fee of $2,500{" "}
                    </span>
                    for infrastructure, warm-up, and Week-1 discovery.
                </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {tiers.map((t, i) => (
                    <motion.div
                        key={t.key}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1, duration: 0.7 }}
                        data-testid={`pricing-card-${t.key}`}
                        className={`price-card ${t.featured ? "featured md:-translate-y-4" : ""}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-mono uppercase text-[0.68rem] tracking-[0.24em] text-white/50">
                                Level {String(i + 1).padStart(2, "0")} ·{" "}
                                {t.name}
                            </span>
                            {t.featured && (
                                <span className="font-mono uppercase text-[0.62rem] tracking-[0.22em] text-[#e84125]">
                                    ● Most chosen
                                </span>
                            )}
                        </div>
                        <div className="flex items-baseline gap-2 mt-2">
                            <span className="font-display text-white text-6xl md:text-7xl leading-none">
                                ${t.price.toLocaleString()}
                            </span>
                            <span className="font-mono uppercase text-[0.72rem] tracking-[0.2em] text-white/50">
                                USD{t.cadence}
                            </span>
                        </div>
                        <p className="font-editorial italic text-white/80 text-lg leading-snug">
                            {t.promise}
                        </p>
                        <div className="border-t border-white/10 pt-4 mt-2">
                            <span className="font-mono uppercase text-[0.62rem] tracking-[0.24em] text-[#e84125] block mb-3">
                                + $2,500 one-time setup
                            </span>
                            <ul className="space-y-3">
                                {t.features.map((f, k) => (
                                    <li
                                        key={k}
                                        className="flex gap-3 font-body text-white/70 text-sm leading-relaxed"
                                    >
                                        <span className="text-[#e84125] mt-0.5">
                                            ▸
                                        </span>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <a
                            href="#contact"
                            data-testid={`pricing-cta-${t.key}`}
                            className={`mt-4 ${t.featured ? "btn-primary" : "btn-ghost"} justify-center text-center`}
                            onClick={() => {
                                try {
                                    sessionStorage.setItem(
                                        "selected_tier",
                                        t.name,
                                    );
                                } catch {
                                    /* noop */
                                }
                            }}
                        >
                            Start {t.name} →
                        </a>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}
